/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx, css } from '@emotion/react';

import type { Layout as ExtensionLayout } from '@atlaskit/adf-schema/extensions';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics/types/enums';
import type {
	ExtensionHandlers,
	ExtensionParams,
} from '@atlaskit/editor-common/extensions/extension-handler';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import overflowShadow from '@atlaskit/editor-common/OverflowShadow';
import type {
	OverflowShadowProps,
	OverflowShadowState,
} from '@atlaskit/editor-common/OverflowShadow';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { Mark as PMMark, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { AnalyticsEventPayload } from '../../analytics/events';
import { RendererCssClassName } from '../../consts';
import { ProvidersContext } from '../../ui/annotations/context';
import { useAnnotationHoverDispatch } from '../../ui/annotations/contexts/AnnotationHoverContext';
import { useAnnotationRangeDispatch } from '../../ui/annotations/contexts/AnnotationRangeContext';
import ExtensionRenderer from '../../ui/ExtensionRenderer';
import type { RendererAppearance } from '../../ui/Renderer/types';
import type { RendererContext, ExtensionViewportSize } from '../types';
import { calcBreakoutWidthCss } from '../utils/breakout';
import { ExtensionComments } from './extension-comments';
import { useNodeHeight, type ScheduleExtensionHeightRemoval } from './useNodeHeight';

interface Props {
	allowAnnotations?: boolean;
	allowAnnotationsDraftMode?: boolean;
	dataAttributes?: {
		'data-renderer-start-pos': number;
	};
	extensionHandlers?: ExtensionHandlers;
	extensionKey: string;
	extensionNode?: PMNode;
	extensionType: string;
	extensionViewportSizes?: ExtensionViewportSize[];
	/**
	 * Extension keys that should render nothing (instead of the default placeholder
	 * text) while their extension provider promise is still pending. This lets the
	 * product opt specific extensions out of showing default content before their
	 * handler has resolved. When omitted, behaviour is unchanged.
	 */
	hideExtensionKeysWhilePending?: string[];
	isInsideOfInlineExtension?: boolean;
	layout?: ExtensionLayout;
	localId?: string;
	marks?: PMMark[];
	nodeHeight?: string;
	nodePresetHeight?: number;
	nodePresetMinHeight?: string;
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	parameters?: any;
	path?: PMNode[];
	providers: ProviderFactory;
	rendererAppearance?: RendererAppearance;
	rendererContext: RendererContext;
	scheduleExtensionHeightRemoval?: ScheduleExtensionHeightRemoval;
	shouldDisplayExtensionAsInline?: (extensionParams?: ExtensionParams<Parameters>) => boolean;
	text?: string;
}

type AllOrNone<T> = T | { [K in keyof T]?: never };

type RenderExtensionOptions = {
	dataAttributes?: Props['dataAttributes'];
	fireAnalyticsEvent?: (event: AnalyticsEventPayload) => void;
	height?: number;
	isCommentTarget?: boolean;
	isTopLevel?: boolean;
	onBlur?: React.FocusEventHandler<HTMLDivElement>;
	onFocus?: React.FocusEventHandler<HTMLDivElement>;
	onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
	onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
	rendererAppearance?: RendererAppearance;
} & AllOrNone<OverflowShadowProps>;

const FORGE_EXTENSION_TYPE = 'com.atlassian.ecosystem';
/**
 * Mirrors `FORGE_INLINE_BODIED_PARAM` in `@atlassian/xen-editor-provider`. Duplicated rather than
 * imported: the renderer must not depend on a Forge package, and this is a stored parameter name,
 * so it is part of the document contract rather than that package's API.
 */
const FORGE_INLINE_BODIED_PARAM = 'atlassianForgeInlineBodied';

const viewportSizes = ['small', 'medium', 'default', 'large', 'xlarge'];
type ViewportSizeType = (typeof viewportSizes)[number];
type ViewportSizeObjectType = {
	[size in ViewportSizeType]: string;
};
// Mirrors sizes from https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/platform/packages/forge/xen-editor-provider/src/render/renderers/ForgeUIExtension.tsx
const macroHeights: ViewportSizeObjectType = {
	small: '112px',
	medium: '262px',
	default: '262px',
	large: '524px',
	xlarge: '1048px',
};

export const hasExtensionViewportSize = (
	extensionId?: string,
	extensionViewportSizes?: ExtensionViewportSize[],
): boolean =>
	Boolean(
		extensionId &&
		extensionViewportSizes?.some(
			(extension) => extension.extensionId === extensionId && Boolean(extension.viewportSize),
		),
	);

const getViewportSize = (
	extensionId?: string,
	extensionViewportSizes?: ExtensionViewportSize[],
) => {
	if (!Array.isArray(extensionViewportSizes) || !extensionId) {
		return;
	}
	const extension = extensionViewportSizes.find(
		(extension) => extension.extensionId === extensionId,
	);
	if (extension) {
		const viewportSize: ViewportSizeType =
			extension.viewportSize && viewportSizes.includes(extension.viewportSize)
				? extension.viewportSize
				: 'default';
		return macroHeights[viewportSize];
	}
};

const containerStyle = css({
	containerType: 'inline-size',
});

type FireExtensionAsInlineAnalyticsProps = {
	fireAnalyticsEvent: NonNullable<RenderExtensionOptions['fireAnalyticsEvent']>;
	node: ExtensionParams<Parameters>;
};

/**
 * Fires an analytics event once on mount when a bodied extension is rendered as an inline element.
 * The node is stored in a ref to avoid re-firing if the node reference changes.
 */
const FireExtensionAsInlineAnalytics = ({
	fireAnalyticsEvent,
	node,
}: FireExtensionAsInlineAnalyticsProps) => {
	const nodeRef = useRef(node);
	useEffect(() => {
		fireAnalyticsEvent({
			action: ACTION.RENDERED,
			actionSubject: ACTION_SUBJECT.EXTENSION_AS_INLINE,
			actionSubjectId: ACTION_SUBJECT_ID.EXTENSION_BODIED,
			attributes: {
				extensionKey: nodeRef.current.extensionKey,
				extensionType: nodeRef.current.extensionType,
			},
			eventType: EVENT_TYPE.OPERATIONAL,
		});
	}, [fireAnalyticsEvent]);
	return null;
};

export const renderExtension = (
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	content: any,
	layout: ExtensionLayout,
	options: RenderExtensionOptions = {},
	removeOverflow?: boolean,
	extensionId?: string,
	extensionViewportSizes?: ExtensionViewportSize[],
	nodePresetMinHeight?: string,
	localId?: string,
	shouldDisplayExtensionAsInline?: (extensionParams?: ExtensionParams<Parameters>) => boolean,
	node?: ExtensionParams<Parameters>,
	isInsideOfInlineExtension?: boolean,
): React.JSX.Element => {
	const overflowContainerClass = !removeOverflow
		? RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER
		: '';

	// by default, we assume the extension is at top level, (direct child of doc node)
	const { isTopLevel = true, rendererAppearance, fireAnalyticsEvent } = options || {};

	// we should only use custom layout for full-page appearance
	const canUseCustomLayout = isExperimentEnabled('platform_editor_remove_important_in_render_ext')
		? rendererAppearance === 'full-page'
		: true;
	const isCustomLayout =
		isTopLevel && ['wide', 'full-width'].includes(layout) && canUseCustomLayout;
	const centerAlignClass = isCustomLayout ? RendererCssClassName.EXTENSION_CENTER_ALIGN : '';
	/**
	 * To reduce cumulative layout shift, we check installed manifest values (viewportSize) for Forge and extension node parameters
	 * for Connect (legacy). As Connect is being phased out, we want Forge to also start to store its expected height
	 * in node parameters, especially for dynamic content macros. LegacyMacroStyledElements implements logic similar to here
	 * as the extension handler in CFE for legacy macros and Connect.
	 */
	const viewportSize = getViewportSize(extensionId, extensionViewportSizes);
	const extensionHeight = nodePresetMinHeight || viewportSize;
	// Forge manifest heights already include px; Connect node heights are numeric strings.
	const extensionMinHeight =
		extensionHeight?.endsWith('px') && fg('confluence_forge_early_render_reserve_height')
			? extensionHeight
			: extensionHeight && `${extensionHeight}px`;
	/**
	 * Scoped to nodes inserted by an app declaring `layout: inline-bodied`, which is what writes
	 * `atlassianForgeInlineBodied`. The output-type marker alone would also match migrated Connect
	 * content — it carries the same marker, and after an upgrade plus a storage round trip in the
	 * same shape — so keying on it would change how existing content renders. The renderer only
	 * ever sees the stored node, never the manifest, so a parameter this code writes is the only
	 * available signal.
	 */
	const hasForgeInlineBodiedMarker = Boolean(
		node?.extensionType === FORGE_EXTENSION_TYPE &&
		node?.content &&
		node?.parameters?.guestParams?.[FORGE_INLINE_BODIED_PARAM] === 'true',
	);
	const isInlineBodiedLayoutEnabled =
		node?.extensionType === FORGE_EXTENSION_TYPE &&
		node?.type === 'bodiedExtension' &&
		node?.parameters?.layout === 'inline-bodied';
	/**
	 * The pass that marks the sibling textblocks around an inline extension resolves their
	 * positions without a depth term, so it only ever matches at the top level. Inlining a nested
	 * container without joining its neighbours leaves a shrink-wrapped box alone on its own line,
	 * which is worse than leaving it a block — so keep nested inline-bodied Forge macros as
	 * blocks until the sibling marking works at depth.
	 */
	const isNestedForgeInlineBodied =
		!isTopLevel && (hasForgeInlineBodiedMarker || isInlineBodiedLayoutEnabled);
	const isInline = shouldDisplayExtensionAsInline?.(node) && !isNestedForgeInlineBodied;
	const inlineClassName = isInline ? RendererCssClassName.EXTENSION_AS_INLINE : '';
	/**
	 * A native Forge macro does not have its body rendered by the product — the body ADF is
	 * sent to the app over the bridge and the app renders it with its own nested renderer.
	 * The inline styling above stops at the outer wrapper, so mark these nodes to let the
	 * nested document's block spacing be collapsed too.
	 */
	const isNativeForgeInline = Boolean(isInline && hasForgeInlineBodiedMarker);
	/**
	 * Migrated inline-bodied macros intentionally do not receive the native Forge marker. Mark the
	 * rendered wrapper instead so the stylesheet can fix only the surrounding text flow without
	 * applying the native nested-renderer, overflow or sizing rules to migrated content.
	 * Custom UI also uses this outer text-flow treatment, without styling inside its iframe.
	 */
	const isMigratedInlineBodied = Boolean(isInline && node?.content && !hasForgeInlineBodiedMarker);

	const asInlineAnalytics =
		isInline && fireAnalyticsEvent && node ? (
			<FireExtensionAsInlineAnalytics fireAnalyticsEvent={fireAnalyticsEvent} node={node} />
		) : null;

	const extensionDiv = (
		<div
			ref={options.handleRef}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={`${RendererCssClassName.EXTENSION} ${inlineClassName} ${options.shadowClassNames} ${centerAlignClass}`}
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Runtime preset height is omitted from the legacy style object.
				...(!isInline && options.height !== undefined ? { height: options.height } : {}),
				width: isInline
					? undefined
					: (
								isExperimentEnabled('platform_editor_remove_important_in_render_ext')
									? isCustomLayout
									: isTopLevel
						  )
						? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							calcBreakoutWidthCss(layout as ExtensionLayout)
						: isExperimentEnabled('platform_editor_remove_important_in_render_ext')
							? undefined
							: '100%',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				minHeight: isInline ? undefined : extensionMinHeight,
			}}
			data-layout={layout}
			data-local-id={localId}
			data-renderer-start-pos={options.dataAttributes?.['data-renderer-start-pos']}
			data-testid="extension--wrapper"
			data-node-type="extension"
			data-inline-comments-target={options.isCommentTarget || undefined}
			data-top-level={isTopLevel || undefined}
			data-forge-inline={isNativeForgeInline || undefined}
			data-migrated-inline={isMigratedInlineBodied || undefined}
			onBlur={options.onBlur}
			onFocus={options.onFocus}
			onMouseEnter={options.onMouseEnter}
			onMouseLeave={options.onMouseLeave}
		>
			<div
				tabIndex={options.tabIndex}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className={`${RendererCssClassName.EXTENSION_INNER_WRAPPER} ${overflowContainerClass}`}
				css={[!isInsideOfInlineExtension && containerStyle]}
			>
				{asInlineAnalytics}
				{content}
			</div>
		</div>
	);
	return centerAlignClass ? (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={
				RendererCssClassName.STICKY_SAFE_CENTER_WRAPPER +
				' ' +
				RendererCssClassName.FLEX_CENTER_WRAPPER
			}
		>
			{extensionDiv}
		</div>
	) : (
		extensionDiv
	);
};

const ExtensionWithAnnotations = (props: React.PropsWithChildren<Props & OverflowShadowProps>) => {
	const providers = useContext(ProvidersContext);
	const { setHoverTarget } = useAnnotationRangeDispatch();
	const { cancelTimeout, initiateTimeout, setIsWithinRange } = useAnnotationHoverDispatch();
	const isBlockNodeSupported = providers?.inlineComment?.isBlockNodeSupported;
	const supportsComments = useMemo(
		() => Boolean(props.extensionNode && isBlockNodeSupported?.(props.extensionNode) === true),
		[props.extensionNode, isBlockNodeSupported],
	);
	const canCreateCommentOnBlockNode = providers?.inlineComment?.canCreateCommentOnBlockNode;
	const isFullPageRenderer =
		props.rendererAppearance === 'full-page' || props.rendererAppearance === 'full-width';
	// The creation policy may fire an experiment exposure. In the full-page renderer it is only
	// checked once the user hovers or focuses the extension, where the comment target appears.
	// Other renderers have no hover entry point, so they keep checking during render. Saved
	// threads stay viewable through `supportsComments`.
	// Only an allowed result is stored. A rejection is not cached because it can be temporary (for
	// example before Statsig initializes), so the check runs again on the next hover or focus.
	const [allowedCommentNode, setAllowedCommentNode] = useState<PMNode>();
	const canCreateDuringRender = useMemo(
		() =>
			!isFullPageRenderer &&
			Boolean(
				supportsComments &&
				props.extensionNode &&
				canCreateCommentOnBlockNode?.(props.extensionNode),
			),
		[isFullPageRenderer, supportsComments, props.extensionNode, canCreateCommentOnBlockNode],
	);
	const isCommentTarget = Boolean(
		supportsComments &&
		props.extensionNode &&
		(!canCreateCommentOnBlockNode ||
			canCreateDuringRender ||
			allowedCommentNode === props.extensionNode),
	);
	const isCommentTargetRef = useRef(isCommentTarget);
	isCommentTargetRef.current = isCommentTarget;
	const resolveCommentTarget = useCallback(
		(target: HTMLElement): boolean => {
			if (isCommentTargetRef.current) {
				return true;
			}
			const node = props.extensionNode;
			if (
				!supportsComments ||
				!node ||
				!canCreateCommentOnBlockNode ||
				!canCreateCommentOnBlockNode(node)
			) {
				return false;
			}
			// The hover target is matched against this attribute before React re-renders, so set it
			// now; the batched render below writes the same value.
			target.dataset.inlineCommentsTarget = 'true';
			isCommentTargetRef.current = true;
			setAllowedCommentNode(node);
			return true;
		},
		[supportsComments, props.extensionNode, canCreateCommentOnBlockNode],
	);
	const onMouseEnter = useCallback<React.MouseEventHandler<HTMLDivElement>>(
		(event) => {
			if (!resolveCommentTarget(event.currentTarget)) {
				return;
			}
			cancelTimeout();
			if (event.buttons === 0) {
				setHoverTarget?.(event.currentTarget);
				setIsWithinRange(true);
			}
		},
		[cancelTimeout, resolveCommentTarget, setHoverTarget, setIsWithinRange],
	);
	const onMouseLeave = useCallback(() => {
		if (isCommentTargetRef.current) {
			initiateTimeout();
		}
	}, [initiateTimeout]);
	const onFocus = useCallback<React.FocusEventHandler<HTMLDivElement>>(
		(event) => {
			if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
				return;
			}
			if (!resolveCommentTarget(event.currentTarget)) {
				return;
			}
			cancelTimeout();
			setHoverTarget?.(event.currentTarget);
			setIsWithinRange(true);
		},
		[cancelTimeout, resolveCommentTarget, setHoverTarget, setIsWithinRange],
	);
	const onBlur = useCallback<React.FocusEventHandler<HTMLDivElement>>(
		(event) => {
			if (
				isCommentTargetRef.current &&
				!event.currentTarget.contains(event.relatedTarget as Node | null)
			) {
				initiateTimeout();
			}
		},
		[initiateTimeout],
	);
	// Listen whenever comments are supported, so the first hover or focus can check creation.
	const commentTargetOptions =
		supportsComments && isFullPageRenderer
			? { isCommentTarget, onBlur, onFocus, onMouseEnter, onMouseLeave }
			: { isCommentTarget };
	const commentProps = {
		enabled: supportsComments,
		allowAnnotations: props.allowAnnotations,
		allowAnnotationsDraftMode: props.allowAnnotationsDraftMode && isCommentTarget,
		startPos: props.dataAttributes?.['data-renderer-start-pos'],
		marks: props.marks,
	};

	return (
		<ExtensionContent
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			commentProps={commentProps}
			commentTargetOptions={commentTargetOptions}
		/>
	);
};

const ExtensionContent = ({
	commentProps = {},
	commentTargetOptions,
	...props
}: React.PropsWithChildren<Props & OverflowShadowProps> & {
	commentProps?: Omit<React.ComponentProps<typeof ExtensionComments>, 'children'>;
	commentTargetOptions?: Pick<
		RenderExtensionOptions,
		'isCommentTarget' | 'onBlur' | 'onFocus' | 'onMouseEnter' | 'onMouseLeave'
	>;
}) => {
	const {
		text,
		layout = 'default',
		handleRef,
		shadowClassNames,
		path = [],
		extensionViewportSizes,
		parameters,
		nodeHeight,
		localId,
		isInsideOfInlineExtension,
	} = props;
	const presetOptions = props.scheduleExtensionHeightRemoval
		? { height: props.nodePresetHeight }
		: {};

	return (
		<ExtensionRenderer
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			type="extension"
		>
			{({ isExtensionProviderPending, result }) => {
				try {
					// Return the result directly if it's a valid JSX.Element
					if (result && React.isValidElement(result)) {
						return renderExtension(
							<ExtensionComments
								enabled={commentProps.enabled}
								allowAnnotations={commentProps.allowAnnotations}
								allowAnnotationsDraftMode={commentProps.allowAnnotationsDraftMode}
								startPos={commentProps.startPos}
								marks={commentProps.marks}
							>
								{result}
							</ExtensionComments>,
							layout,
							{
								...presetOptions,
								dataAttributes: props.dataAttributes,
								isTopLevel: path.length < 1,
								handleRef,
								shadowClassNames,
								tabIndex: props.tabIndex,
								rendererAppearance: props.rendererAppearance,
								...commentTargetOptions,
							},
							undefined,
							parameters?.extensionId,
							extensionViewportSizes,
							nodeHeight,
							localId,
							undefined,
							undefined,
							isInsideOfInlineExtension,
						);
					}
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
				} catch (e) {
					/** We don't want this error to block renderer */
					/** We keep rendering the default content */
				}

				if (
					isExtensionProviderPending &&
					props.hideExtensionKeysWhilePending?.includes(props.extensionKey)
				) {
					return <React.Fragment />;
				}

				// Always return default content if anything goes wrong
				return renderExtension(
					<ExtensionComments
						enabled={commentProps.enabled}
						allowAnnotations={commentProps.allowAnnotations}
						allowAnnotationsDraftMode={commentProps.allowAnnotationsDraftMode}
						startPos={commentProps.startPos}
						marks={commentProps.marks}
					>
						{text || 'extension'}
					</ExtensionComments>,
					layout,
					{
						...presetOptions,
						dataAttributes: props.dataAttributes,
						isTopLevel: path.length < 1,
						handleRef,
						shadowClassNames,
						tabIndex: props.tabIndex,
						rendererAppearance: props.rendererAppearance,
						...commentTargetOptions,
					},
					undefined,
					parameters?.extensionId,
					extensionViewportSizes,
					nodeHeight,
					localId,
					undefined,
					undefined,
					isInsideOfInlineExtension,
				);
			}}
		</ExtensionRenderer>
	);
};

const ExtensionView = (props: React.PropsWithChildren<Props & OverflowShadowProps>) => {
	// Keep annotation context subscriptions out of the gate-off render path.
	if (fg('cc_maui_annotations_on_extensions')) {
		// eslint-disable-next-line react/jsx-props-no-spreading
		return <ExtensionWithAnnotations {...props} />;
	}
	// eslint-disable-next-line react/jsx-props-no-spreading
	return <ExtensionContent {...props} />;
};

const ExtensionWithTemporarySizing = (
	props: React.PropsWithChildren<Props & OverflowShadowProps>,
) => {
	const { presetMinHeight, presetHeight, hasExpired } = useNodeHeight({
		nodePresetMinHeight: props.nodePresetMinHeight,
		node: props.extensionNode,
		scheduleRemoval: props.scheduleExtensionHeightRemoval,
		nodePresetHeight: props.nodePresetHeight,
		hasViewportSize: hasExtensionViewportSize(
			props.parameters?.extensionId,
			props.extensionViewportSizes,
		),
	});
	return (
		<ExtensionView
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			nodeHeight={presetMinHeight}
			nodePresetHeight={presetHeight}
			extensionViewportSizes={
				hasExpired || presetHeight !== undefined ? undefined : props.extensionViewportSizes
			}
		/>
	);
};

const Extension = (props: React.PropsWithChildren<Props & OverflowShadowProps>) => {
	if (props.scheduleExtensionHeightRemoval) {
		// eslint-disable-next-line react/jsx-props-no-spreading
		return <ExtensionWithTemporarySizing {...props} />;
	}
	// eslint-disable-next-line react/jsx-props-no-spreading
	return <ExtensionView {...props} />;
};

const _default_1: React.ComponentClass<Props & OverflowShadowProps, OverflowShadowState> =
	overflowShadow(Extension, {
		overflowSelector: `.${RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER}`,
	});
export default _default_1;
